import {
    createAsyncThunk,
    createSelector,
    createSlice,
    Dispatch,
    isAnyOf,
} from "@reduxjs/toolkit";
import {
    ComposerTemplate,
    EvaluationQuestionFromJSON,
    EvaluationQuestionTypeEnum,
    TemplateEntityListing,
} from "@genesys/cloud-frontend/dist/mjs/models";
import { RootState } from "app/store";
import {
    GetEvaluationsTemplatesExpandEnum,
    QualityApi,
} from "@genesys/cloud-frontend/dist/mjs/apis/QualityApi";
import { denormalize, normalize } from "normalizr";
import {
    normalizedTemplates,
    questions,
} from "features/qualityForm/qualityFormNormalizationSchemas";
import { API_CONFIGURATION } from "util/apiUtils";

export type TemplateState = {
    data: {
        templates?: Record<string, ComposerTemplate | undefined>;
        favoriteTemplates: string[];
    };
};

const initialState: TemplateState = {
    data: {
        favoriteTemplates: [],
    },
};

export const getTemplates = createAsyncThunk<
    Promise<TemplateEntityListing>,
    { isAutomatedEvaluationForm: boolean },
    { state: RootState }
>("qualityForm/getTemplates", async ({ isAutomatedEvaluationForm }) => {
    const qualityApi = new QualityApi(API_CONFIGURATION);
    const templates = await qualityApi.getEvaluationsTemplates({
        tags: "question",
        expand: GetEvaluationsTemplatesExpandEnum.Data,
        pageSize: 500,
    });
    if (isAutomatedEvaluationForm && templates.entities) {
        // Filter out multiple select questions for automated evaluations
        templates.entities = templates.entities?.filter((template) => {
            const templateType = template.data?.type
                ? String(template.data.type)
                : undefined;
            return (
                templateType !==
                EvaluationQuestionTypeEnum.MultipleSelectQuestion
            );
        });
    }
    return templates;
});

export const getFavoriteTemplates = createAsyncThunk<
    ComposerTemplate[],
    undefined,
    { state: RootState }
>("qualityForm/getFavoriteTemplates", async () => {
    const qualityApi = new QualityApi(API_CONFIGURATION);
    return qualityApi.getEvaluationsFavoritetemplates();
});

export const saveQuestionAsTemplate = createAsyncThunk<
    ComposerTemplate,
    { templateName: string; questionId: string; setFavorite: boolean },
    { state: RootState }
>("qualityForm/saveQuestionAsTemplate", async (arg, { getState, dispatch }) => {
    const state: RootState = getState();
    const questionData = state.qualityForm.data.questions?.[arg.questionId];

    // omit original questionId and visibility condition from question template before saving
    // this will prevent using that questionId when adding that template question
    const { id, visibilityCondition, ...denormalizedQuestion } =
        EvaluationQuestionFromJSON(
            denormalize(arg.questionId, questions, {
                questions: { [arg.questionId]: questionData },
                answerOptions: state.qualityForm.data.answerOptions,
                multipleSelectOptionQuestions:
                    state.qualityForm.data.multipleSelectOptionQuestions,
            })
        ) as {
            [key: string]: object;
        };

    const qualityApi = new QualityApi(API_CONFIGURATION);
    if (questionData && questionData?.id) {
        return qualityApi
            .postEvaluationsTemplates({
                body: {
                    data: denormalizedQuestion,
                    name: arg.templateName,
                    tags: ["question"],
                },
            })
            .then((response) => {
                if (arg.setFavorite && response.id) {
                    dispatch(setFavoriteTemplate({ templateId: response.id }));
                }
                return response;
            });
    }
    throw new Error("Template cannot be saved");
});

export const deleteTemplate = createAsyncThunk<
    string,
    { templateId: string },
    { state: RootState }
>("qualityForm/deleteTemplate", async (arg) => {
    const qualityApi = new QualityApi(API_CONFIGURATION);
    qualityApi.deleteEvaluationsTemplate({
        templateId: arg.templateId,
    });
    return arg.templateId;
});
export const handleDeleteTemplate =
    (templateId: string) =>
    (dispatch: Dispatch<any>, getState: () => RootState) => {
        if (selectIsTemplateIdFavorite(getState(), templateId)) {
            dispatch(unsetFavouriteTemplate({ templateId }));
        }
        dispatch(deleteTemplate({ templateId }));
    };

export const setFavoriteTemplate = createAsyncThunk<
    string,
    { templateId: string },
    { state: RootState }
>("qualityForm/setFavoriteTemplate", async (arg) => {
    const qualityApi = new QualityApi(API_CONFIGURATION);
    qualityApi.postEvaluationsFavoritetemplates({
        body: {
            templateId: arg.templateId,
        },
    });
    return arg.templateId;
});

export const unsetFavouriteTemplate = createAsyncThunk<
    string,
    { templateId: string },
    { state: RootState }
>("qualityForm/unsetFavoriteTemplate", async (arg) => {
    const qualityApi = new QualityApi(API_CONFIGURATION);
    qualityApi.deleteEvaluationsFavoritetemplate({
        templateId: arg.templateId,
    });
    return arg.templateId;
});

const templateSlice = createSlice({
    name: "counter",
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(saveQuestionAsTemplate.fulfilled, (state, { payload }) => {
                if (payload && payload.id) {
                    state.data.templates = {
                        ...state.data.templates,
                        [payload.id]: payload,
                    };
                }
            })
            .addCase(getTemplates.fulfilled, (state, { payload }) => {
                const { entities } = normalize(payload, normalizedTemplates);
                state.data.templates = { ...entities.templates };
            })
            .addCase(getFavoriteTemplates.fulfilled, (state, { payload }) => {
                const isValid = (id: string | undefined): id is string => {
                    return !!id;
                };
                const ids = payload.map(({ id }) => id).filter(isValid);

                if (ids) {
                    state.data.favoriteTemplates = ids;
                }
            })
            .addCase(deleteTemplate.fulfilled, (state, { payload }) => {
                delete state.data.templates?.[payload];
            })
            .addCase(setFavoriteTemplate.fulfilled, (state, { payload }) => {
                state.data.favoriteTemplates.push(payload);
            })
            .addCase(unsetFavouriteTemplate.fulfilled, (state, { payload }) => {
                state.data.favoriteTemplates =
                    state.data.favoriteTemplates.filter(
                        (templateId) => templateId !== payload
                    );
            })
            .addMatcher(
                isAnyOf(
                    saveQuestionAsTemplate.rejected,
                    deleteTemplate.rejected,
                    getFavoriteTemplates.rejected,
                    getTemplates.rejected,
                    saveQuestionAsTemplate.rejected
                ),
                () => {
                    console.log("error: template operation did not succeed");
                }
            );
    },
});

export const templateSliceActions = templateSlice.actions;

export const selectTemplate = (state: RootState, templateId: string) => {
    return state.template.data.templates?.[templateId];
};
export const selectUnFavouredTemplateIds = (state: RootState) => {
    const templates = state.template.data.templates;
    const favouriteTemplateIds = state.template.data.favoriteTemplates;
    const isValid = (id: string | undefined): id is string => {
        return !!id && !favouriteTemplateIds.includes(id);
    };
    return Object.keys(templates ?? {}).filter(isValid);
};
export const selectFavouriteTemplateIds = (state: RootState) => {
    // favorite template api returns ids which aren't always valid. Hence we are checking those ids here by referring to ids in templates, which is our golden source of truth.

    const templates = Object.keys(state.template.data.templates ?? {});
    const isValid = (id: string | undefined): id is string => {
        return !!id && templates.includes(id);
    };
    return state.template.data.favoriteTemplates.filter(isValid);
};

const selectMemoizedFavouriteTemplateIds = createSelector(
    (state: RootState) => state.template.data.favoriteTemplates,
    (favouriteTemplateIds) => new Set(favouriteTemplateIds)
);
export const selectIsTemplateIdFavorite = (
    state: RootState,
    templateId: string
) => selectMemoizedFavouriteTemplateIds(state).has(templateId);

export default templateSlice.reducer;
